import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * ============================================
 * Server Health Check Hook
 * ============================================
 *
 * Detects if Server is available (calls /api/health endpoint)
 *
 * Features:
 * 1. Initial check: Multiple checks on startup (fault tolerance)
 * 2. Auto retry: Uses exponential backoff strategy after failure
 * 3. Manual retry: Provides retry() method
 * 4. Status recovery: Auto updates status when recovery detected
 *
 * Optimizations:
 * - Initial check supports multiple quick retries, avoids temporary network fluctuations triggering maintenance page
 * - Only marks unavailable after consecutive failures
 */

export type ServerStatus = 'checking' | 'available' | 'unavailable';

export interface HealthCheckResponse {
  status: string;
  timestamp: string;
  version: string;
  uptime: number;
}

/**
 * Server response wrapper format
 * ResponseInterceptor wraps response as { success: true, data: {...} }
 */
interface WrappedResponse<T> {
  success: boolean;
  data: T;
}

export interface UseServerHealthOptions {
  /** Whether to enable auto retry (default true) */
  autoRetry?: boolean;
  /** Initial check max retry count (default 3) - Quick retries during initial phase to avoid temporary network fluctuations */
  initialRetryCount?: number;
  /** Initial check retry interval (ms, default 2000) - Shorter interval during initial phase */
  initialRetryDelay?: number;
  /** Subsequent retry interval (ms, default 5000) - Retry interval after confirmed unavailable */
  retryInterval?: number;
  /** Max retry interval (ms, default 30000) */
  maxRetryInterval?: number;
  /** Retry interval growth factor (default 1.5) */
  backoffMultiplier?: number;
  /** Callback when status becomes available */
  onAvailable?: () => void;
  /** Callback when status becomes unavailable */
  onUnavailable?: (error: Error) => void;
}

export interface UseServerHealthReturn {
  /** Current server status */
  status: ServerStatus;
  /** Whether checking */
  isChecking: boolean;
  /** Whether in initial checking phase (with fault tolerance retries) */
  isInitialChecking: boolean;
  /** Whether service is available */
  isAvailable: boolean;
  /** Whether service is unavailable */
  isUnavailable: boolean;
  /** Most recent error */
  error: Error | null;
  /** Seconds until next auto retry (0 means no auto retry) */
  retryIn: number;
  /** Health check response data */
  healthData: HealthCheckResponse | null;
  /** Manually trigger retry */
  retry: () => Promise<void>;
}

/**
 * API Base URL configuration
 * - Development: /api (proxied by Vite to localhost:3001)
 * - Production: https://api.studyjoyful.com/api
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Create an independent axios instance for health checks
 * Don't use apiClient to avoid auth-related interference
 */
const healthClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 second timeout (increased tolerance)
});

/**
 * Delay function
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useServerHealth(
  options: UseServerHealthOptions = {}
): UseServerHealthReturn {
  const {
    autoRetry = true,
    initialRetryCount = 3, // Initial check max 3 retries
    initialRetryDelay = 2000, // Initial check interval 2 seconds (quick retry)
    retryInterval = 5000, // Subsequent retry interval 5 seconds
    maxRetryInterval = 30000,
    backoffMultiplier = 1.5,
    onAvailable,
    onUnavailable,
  } = options;

  const [status, setStatus] = useState<ServerStatus>('checking');
  const [error, setError] = useState<Error | null>(null);
  const [healthData, setHealthData] = useState<HealthCheckResponse | null>(null);
  const [retryIn, setRetryIn] = useState(0);
  // Whether currently in initial checking phase
  const [isInitialChecking, setIsInitialChecking] = useState(true);

  // Save current retry interval
  const currentIntervalRef = useRef(retryInterval);
  // Retry timer
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Countdown timer
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Save callback refs (avoid useEffect dependency issues)
  const onAvailableRef = useRef(onAvailable);
  const onUnavailableRef = useRef(onUnavailable);
  // Whether mounted
  const mountedRef = useRef(true);

  // Update callback refs
  useEffect(() => {
    onAvailableRef.current = onAvailable;
    onUnavailableRef.current = onUnavailable;
  }, [onAvailable, onUnavailable]);

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setRetryIn(0);
  }, []);

  /**
   * Execute single health check (doesn't change status)
   * @returns true if successful
   */
  const singleHealthCheck = useCallback(async (): Promise<boolean> => {
    try {
      // Server response may be wrapped format { success: true, data: {...} }
      const response = await healthClient.get<
        WrappedResponse<HealthCheckResponse> | HealthCheckResponse
      >('/health');

      // Check wrapped format (Server ResponseInterceptor)
      const data = response.data;
      const health =
        'success' in data && data.success
          ? data.data
          : (data as HealthCheckResponse);

      if (health?.status === 'ok') {
        setHealthData(health);
        setError(null);
        return true;
      }

      throw new Error('Health check returned non-ok status');
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Health check failed');
      setError(e);
      return false;
    }
  }, []);

  /**
   * Execute health check and update status
   * Used for manual retry and subsequent auto retries
   */
  const checkHealth = useCallback(async (): Promise<boolean> => {
    const success = await singleHealthCheck();

    if (success) {
      setStatus('available');
      setIsInitialChecking(false);
      // Reset retry interval
      currentIntervalRef.current = retryInterval;
      clearTimers();
      onAvailableRef.current?.();
      return true;
    }

    setStatus('unavailable');
    setIsInitialChecking(false);
    onUnavailableRef.current?.(error ?? new Error('Health check failed'));
    return false;
  }, [singleHealthCheck, retryInterval, clearTimers, error]);

  /**
   * Schedule next retry (use ref to avoid circular dependency)
   */
  const scheduleRetryRef = useRef<(() => void) | undefined>(undefined);

  const scheduleRetry = useCallback(() => {
    if (!autoRetry) return;

    clearTimers();

    const interval = currentIntervalRef.current;
    const seconds = Math.ceil(interval / 1000);
    setRetryIn(seconds);

    // Countdown
    countdownTimerRef.current = setInterval(() => {
      setRetryIn((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Schedule retry
    retryTimerRef.current = setTimeout(async () => {
      const success = await checkHealth();
      if (!success) {
        // Increase retry interval (exponential backoff)
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * backoffMultiplier,
          maxRetryInterval
        );
        scheduleRetryRef.current?.();
      }
    }, interval);
  }, [autoRetry, backoffMultiplier, maxRetryInterval, checkHealth, clearTimers]);

  // Update ref
  scheduleRetryRef.current = scheduleRetry;

  /**
   * Manual retry
   */
  const retry = useCallback(async () => {
    clearTimers();
    setStatus('checking');
    const success = await checkHealth();
    if (!success && autoRetry) {
      scheduleRetry();
    }
  }, [checkHealth, autoRetry, scheduleRetry, clearTimers]);

  /**
   * Initial check: Supports multiple quick retries
   * Only marks unavailable after consecutive failures
   */
  useEffect(() => {
    mountedRef.current = true;

    const runInitialCheck = async () => {
      setIsInitialChecking(true);

      for (let attempt = 1; attempt <= initialRetryCount; attempt++) {
        if (!mountedRef.current) return;

        console.log(
          `[ServerHealth] Initial check attempt ${attempt}/${initialRetryCount}`
        );
        const success = await singleHealthCheck();

        if (success) {
          if (!mountedRef.current) return;
          console.log('[ServerHealth] Server available');
          setStatus('available');
          setIsInitialChecking(false);
          currentIntervalRef.current = retryInterval;
          onAvailableRef.current?.();
          return;
        }

        // If not last attempt, wait and retry
        if (attempt < initialRetryCount) {
          console.log(
            `[ServerHealth] Attempt ${attempt} failed, retrying in ${initialRetryDelay}ms...`
          );
          await delay(initialRetryDelay);
        }
      }

      // All initial retries failed
      if (!mountedRef.current) return;

      console.log(
        `[ServerHealth] All ${initialRetryCount} attempts failed, marking unavailable`
      );
      setStatus('unavailable');
      setIsInitialChecking(false);
      onUnavailableRef.current?.(new Error('Health check failed'));

      // Start subsequent auto retries
      if (autoRetry) {
        scheduleRetryRef.current?.();
      }
    };

    runInitialCheck();

    return () => {
      mountedRef.current = false;
      clearTimers();
    };
    // Only execute once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    isChecking: status === 'checking',
    isInitialChecking,
    isAvailable: status === 'available',
    isUnavailable: status === 'unavailable',
    error,
    retryIn,
    healthData,
    retry,
  };
}

export default useServerHealth;
